import { useEffect, useState } from 'react'

import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import {
  AnimatedBid,
  CurrencyStringDollars,
  FormattedMessage,
  LinkAsButton,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { SkeletonTextLine } from '../../ListingCardSkeleton/ui'
import type { BaseListingCardProps } from '../types'
import DividerSeparatedList from './DividerSeparatedList'

const PriceLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`

const CurrentBid = styled.div`
  width: 100%;
  ${Typography.Title3}
  color: ${Colors.Semantic.Neutral.black};
`

const AdditionalDetails = styled(DividerSeparatedList)`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const ViewDetailsPsuedoLink = styled(LinkAsButton).attrs({ as: 'span' })`
  ${Typography.Body3}
`

const PriceDetails = ({
  listing,
  currentPrice,
  hasTermsRestrictions,
}: Pick<
  BaseListingCardProps,
  'listing' | 'currentPrice' | 'hasTermsRestrictions'
>) => {
  // NaN never equals itself, so a non-finite price (absent upstream data
  // coerced with Number()) would make the effect below setState on every
  // commit, forever. Normalize once rather than trusting every caller.
  const price = Number.isFinite(currentPrice) ? currentPrice : 0

  const [priceState, setPriceState] = useState({
    previous: price,
    current: price,
  })

  useEffect(() => {
    if (price !== priceState.current) {
      // oxlint-disable-next-line react/set-state-in-effect
      setPriceState({
        previous: priceState.current,
        current: price,
      })
    }
  }, [priceState, price])

  const percentOfMsrp = listing.retailPrice ? price / listing.retailPrice : null
  const pricePerUnit = listing.units ? price / listing.units : null

  return (
    <PriceLayout>
      {listing.listingType === 'CONTRACT' ||
      listing.pricingStrategy === 'MAKE_AN_OFFER' ||
      hasTermsRestrictions ? (
        <ViewDetailsPsuedoLink data-testid="view-price">
          <FormattedMessage id="Auction.BidAmount.viewDetails" />
        </ViewDetailsPsuedoLink>
      ) : (
        <>
          <CurrentBid data-testid="price">
            <AnimatedBid
              startValue={priceState.previous}
              endValue={priceState.current}
              currency={listing.currency}
            />
          </CurrentBid>
          <AdditionalDetails
            values={[
              {
                key: 'percent-of-msrp',
                node: percentOfMsrp ? (
                  <FormattedMessage
                    id="Auction.BidAmount.PercentOfMsrp"
                    values={{
                      value: (
                        <FormattedNumber
                          value={percentOfMsrp}
                          style="percent"
                          minimumFractionDigits={1}
                          maximumFractionDigits={1}
                        />
                      ),
                    }}
                  />
                ) : null,
              },
              {
                key: 'price-per-unit',
                node: pricePerUnit ? (
                  <FormattedMessage
                    id="Auction.BidAmount.PerUnit"
                    values={{
                      value: (
                        <CurrencyStringDollars
                          currency={listing.currency}
                          value={pricePerUnit}
                        />
                      ),
                    }}
                  />
                ) : null,
              },
            ]}
          />
        </>
      )}
    </PriceLayout>
  )
}

export default PriceDetails

export const PriceDetailsSkeleton = () => {
  return (
    <PriceLayout>
      <CurrentBid>
        <SkeletonTextLine width="5rem" testIdSuffix="current-bid" />
      </CurrentBid>
      <AdditionalDetails
        values={[
          {
            key: 'placeholder',
            node: (
              <SkeletonTextLine
                width="11rem"
                testIdSuffix="per-unit--percent-of-msrp"
              />
            ),
          },
        ]}
      />
    </PriceLayout>
  )
}
